import { getStorageItem } from './storageManager';

/**
 * This helper is the single source of truth for determining whether a user is considered subscribed.
 * Do not use user.isSubscribed or the isUserSubscribed flag directly for entitlement checks.
 * Always use this helper instead.
 */

const ACTIVE_SUBSCRIPTION_STATUSES = ["COMPLETED", "DEFERRED_CANCELLATION", "PAUSED"];

const getUserSubscriptionStatus = (subscriptionStatus?: string, user?: any): string | undefined => {
  if (subscriptionStatus) return subscriptionStatus;
  const userData = user ?? getStorageItem('AuthenticationStore')?.user;
  return (
    userData?.subscription?.subscriptionStatus ??
    userData?.subscription?.subscriptionInfo?.subscriptionStatus
  );
};

export const isUserSubscribed = (subscriptionStatus?: string, user?: any): boolean => {
  if (user) return user?.isSubscribed;
  const userData = getStorageItem('AuthenticationStore')?.user;
  const userSubscriptionStatus = getUserSubscriptionStatus(subscriptionStatus, userData);

  if (userSubscriptionStatus) return Boolean(ACTIVE_SUBSCRIPTION_STATUSES.includes(userSubscriptionStatus));
  return Boolean(userData?.isSubscribed);
};

/**True when the user's subscriptionStatus is PAUSED (blocks purchase/upgrade). **/
export const isSubscriptionPaused = (subscriptionStatus?: string, user?: any): boolean => {
  return getUserSubscriptionStatus(subscriptionStatus, user) === 'PAUSED';
};

/**
 * Determines whether the user has access to the content's SVOD plans.
 *
 * Returns `true` if:
 * - The user is a VIP, or
 * - The user has an active subscription that matches one of the content's
 *   SVOD plans (including plans resolved from plan groups).
 *
 * Otherwise, returns `false`.
 */
export const isUserEligibleForPlan = (user: any, plans: any) => {
  const planGroupList = JSON.parse(localStorage.getItem("planGroupList")!);
  let monetisationPlans = plans?.length
    ? plans?.filter((content:any) => content?.type === "SVOD")
    : [];
  let monetisationPlansIds = monetisationPlans.map((plan:any) => plan?.planIds);
  let monetisationGroupIds = monetisationPlans.map(
    (plan:any) => plan?.planGroupIds,
  );
  monetisationGroupIds?.forEach((groupId:any) => {
    if (groupId) {
      monetisationPlansIds.push(planGroupList[groupId]);
    }
  });
  let userPlanId = user?.subscription?.subscriptionInfo?.planId;
  let subscriptionStatus =
    user?.subscription?.subscriptionInfo?.subscriptionStatus;
  if (user?.isVip) {
    return true;
  }
  if (monetisationPlansIds?.length) {
    if (isUserSubscribed()) {
      let isSvodPlanSubscribed = false;
      monetisationPlansIds?.filter((plan:any) => {
        if (plan?.includes("svodAll")) {
          isSvodPlanSubscribed = true;
        }
      });
      if (isSvodPlanSubscribed) return isSvodPlanSubscribed;
    }
    let isPlanSubscribed = false;
    monetisationPlansIds?.filter((plan:any) => {
      if (plan?.includes(userPlanId) && isUserSubscribed(subscriptionStatus)) {
        isPlanSubscribed = true;
      }
    });
    return isPlanSubscribed;
  } else {
    return false;
  }
};

/**
 * Returns whether the purchase/upgrade flow should be blocked.
 * - Subscribed users are blocked only if their subscription is paused.
 * - Subscribed/Non-subscribed users are evaluated based on the available plans.
 */
export const shouldBlockPurchase = (user: any, plans: any): boolean => {
  return isSubscriptionPaused() || isUserEligibleForPlan(user, plans);
};


